/**
 * SubClientTile — sub-tenant management tile. Design contract §5.6.
 *
 * Header: 40 px initials monogram on warm canvas. NO app mark, NO red dot.
 * Body: JetBrains Mono slug | serif display name | sans context (≤2 lines)
 * Badge: provisioned (forest) | pending (warn) | suspended (stone)
 * CTA:   ghost "Manage" → /admin/sub-clients/:slug (placeholder in cycle 2)
 */

export type SubClientLifecycle = 'provisioned' | 'pending' | 'suspended';

export interface SubClientData {
  slug: string;
  name: string;
  context?: string;
  lifecycle: SubClientLifecycle;
}

interface SubClientTileProps {
  data: SubClientData;
  onManage?: (slug: string) => void;
}

const LIFECYCLE_BADGE: Record<SubClientLifecycle, { label: string; bg: string; fg: string }> = {
  provisioned: { label: 'Provisioned', bg: 'var(--dp-forest-100)', fg: 'var(--dp-forest-700)' },
  pending:     { label: 'Pending',     bg: 'var(--dp-warn-100)',   fg: 'var(--dp-warn-700)'   },
  suspended:   { label: 'Suspended',   bg: 'var(--dp-stone-200)',  fg: 'var(--dp-stone-700)'  },
};

function Monogram({ name }: { name: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase()).join('');
  return (
    <div
      aria-hidden="true"
      style={{
        width: 40, height: 40,
        borderRadius: 'var(--radius-md)',
        background: 'var(--dp-navy-800)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--fg-on-navy)',
        fontFamily: 'var(--font-serif)',
        fontSize: 'var(--fs-h4)', fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

export default function SubClientTile({ data, onManage }: SubClientTileProps) {
  const badge = LIFECYCLE_BADGE[data.lifecycle];

  return (
    <article
      style={{
        minWidth: 280,
        background: 'var(--bg-surface)',
        borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        transition: `transform var(--dur-base) var(--ease-out),
                     box-shadow var(--dur-base) var(--ease-out),
                     border-color var(--dur-base) var(--ease-out)`,
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.transform = 'translateY(-2px)';
        el.style.boxShadow = 'var(--shadow-2)';
        el.style.borderColor = 'var(--dp-stone-400)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.transform = '';
        el.style.boxShadow = '';
        el.style.borderColor = 'var(--border-default)';
      }}
    >
      {/* Header — warm canvas strip */}
      <div
        style={{
          height: 80,
          background: 'linear-gradient(180deg, var(--dp-canvas-200) 0%, var(--dp-canvas-100) 100%)',
          display: 'flex', alignItems: 'center',
          padding: 'var(--sp-4) var(--sp-5)', gap: 'var(--sp-3)',
        }}
      >
        <Monogram name={data.name} />
      </div>

      {/* Body */}
      <div style={{ padding: 'var(--sp-4) var(--sp-5)', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Lifecycle badge — top-right */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--sp-2)' }}>
          <span style={{
            fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-meta)',
            fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
            background: badge.bg, color: badge.fg,
            borderRadius: 'var(--radius-sm)', padding: '2px 7px',
          }}>
            {badge.label}
          </span>
        </div>

        {/* Slug */}
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-1)', margin: '0 0 var(--sp-1)' }}>
          {data.slug}
        </p>

        {/* Display name */}
        <p style={{ fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-h4)', fontWeight: 600, color: 'var(--fg-1)', margin: '0 0 var(--sp-1)' }}>
          {data.name}
        </p>

        {/* Context — sans, ≤ 2 lines */}
        {data.context && (
          <p style={{
            fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-3)',
            margin: 0,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {data.context}
          </p>
        )}
      </div>

      {/* CTA */}
      <div style={{ padding: 'var(--sp-3) var(--sp-5)', borderTopWidth: '1px', borderTopStyle: 'solid', borderTopColor: 'var(--border-subtle)' }}>
        <button className="dp-btn dp-btn--ghost" onClick={() => onManage?.(data.slug)} style={{ width: '100%', justifyContent: 'center' }}>
          Manage
        </button>
      </div>
    </article>
  );
}
